import { stripDiagnostic } from './utils/signInDiagnostic';
import { scrubAddresses } from './utils/sentryScrub';
import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import './index.css';
import App from './App.jsx';
import ErrorBoundary from './components/ErrorBoundary';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';

if (process.env.REACT_APP_SENTRY_DSN) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.NODE_ENV,
    tracesSampleRate: 0.2,
    beforeSend: (event) => scrubAddresses(stripDiagnostic(event)),
    beforeSendTransaction: scrubAddresses,
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'Non-Error promise rejection captured',
      'Failed to fetch',          // transient mobile network / deployment restarts
      'NetworkError',
      'Load failed',              // Safari equivalent of Failed to fetch
      // A read given up on purpose after 20 s (utils/authFetch giveUpOnAHungRead): the app keeps what
      // it shows and tries again. Seen when the app sits in the background (owner, 21 October 2026).
      'signal is aborted without reason',
      'AbortError',
    ],
  });
}

// Promo deep-link: capture ?promo=CODE on first load and stash it. It has to
// survive the whole signup + 15-step onboarding (and any OAuth full-page
// redirect) before PaymentGate can auto-apply it, so localStorage, not state.
// PaymentGate clears it once it has been used.
try {
  const promo = new URLSearchParams(window.location.search).get('promo');
  if (promo) {
    localStorage.setItem('cipher_promo_code', promo.trim().toUpperCase().slice(0, 32));
    const url = new URL(window.location.href);
    url.searchParams.delete('promo');
    window.history.replaceState({}, '', url.pathname + url.search + url.hash);
  }
} catch {
  /* localStorage or URL API unavailable — promo can still be entered by hand */
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <App />
      <Analytics />
      <SpeedInsights />
    </ErrorBoundary>
  </React.StrictMode>
);

// Auto-hide scrollbar — show on scroll, hide when stopped
let scrollHideTimer;
document.addEventListener('scroll', () => {
  document.documentElement.classList.add('is-scrolling');
  clearTimeout(scrollHideTimer);
  scrollHideTimer = setTimeout(() => {
    document.documentElement.classList.remove('is-scrolling');
  }, 600);
}, { capture: true, passive: true });

// Register service worker for PWA support — production only. Registering
// it in dev fights with webpack's hot-reload dev server: the SW caches
// bundles and keeps re-registering itself on every page load, so edited
// code can keep running from a stale cached bundle no matter how many
// times the page is reloaded.
if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/service-worker.js')
      .catch((error) => {
        console.error('[SW] Registration failed:', error);
      });
  });
}
